import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ServiceChannels } from '@klaro7/shared-types';
import { toast } from 'sonner';

import { extractErrorMessage } from '@/lib/api';
import { useMyProfessionalProfile } from './useProfessionalProfile';
import { updateMyServiceChannels } from '../services/professional-profile.service';
import {
  getCachedProfessional,
  getCachedServiceChannels,
  MY_PROFESSIONAL_PROFILE_QUERY_KEY,
  patchCachedProfessional,
} from '../lib/profileCache';

export type ServiceChannelKey = keyof ServiceChannels;

export function useProfessionalServiceChannels() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useMyProfessionalProfile();
  const [saved, setSaved] = useState(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const mutation = useMutation({
    mutationKey: ['professional-profile', 'service-channels'],
    mutationFn: updateMyServiceChannels,
    onMutate: async (patch) => {
      setSaved(false);
      await queryClient.cancelQueries({ queryKey: MY_PROFESSIONAL_PROFILE_QUERY_KEY });
      const previous = queryClient.getQueryData(MY_PROFESSIONAL_PROFILE_QUERY_KEY);
      const nextChannels = { ...getCachedServiceChannels(previous), ...patch };
      queryClient.setQueryData(
        MY_PROFESSIONAL_PROFILE_QUERY_KEY,
        patchCachedProfessional(previous, { serviceChannels: nextChannels })
      );
      return {
        previous,
        previousOnlineStatus: getCachedProfessional(previous)?.onlineStatus,
      };
    },
    onSuccess: (professional, patch, context) => {
      const persistedStatus = getCachedProfessional(professional)?.onlineStatus;
      queryClient.setQueryData(
        MY_PROFESSIONAL_PROFILE_QUERY_KEY,
        (old: unknown) => {
          const currentStatus = getCachedProfessional(old)?.onlineStatus;
          return patchCachedProfessional(old, {
            serviceChannels: { ...getCachedServiceChannels(old), ...patch },
            ...(persistedStatus && currentStatus === context?.previousOnlineStatus
              ? { onlineStatus: persistedStatus }
              : {}),
          });
        }
      );
      setSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 1800);
    },
    onError: (error, patch, context) => {
      if (context?.previous !== undefined) {
        const previousChannels = getCachedServiceChannels(context.previous);
        queryClient.setQueryData(
          MY_PROFESSIONAL_PROFILE_QUERY_KEY,
          (old: unknown) => {
            const rolledBackChannels = { ...getCachedServiceChannels(old) };
            (Object.keys(patch) as ServiceChannelKey[]).forEach((channel) => {
              rolledBackChannels[channel] = previousChannels[channel];
            });
            return patchCachedProfessional(old, { serviceChannels: rolledBackChannels });
          }
        );
      }
      toast.error('Canale non aggiornato', { description: extractErrorMessage(error) });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_PROFESSIONAL_PROFILE_QUERY_KEY });
    },
  });

  const setChannel = useCallback(
    (channel: ServiceChannelKey, enabled: boolean) => {
      if (mutation.isPending) return;
      mutation.mutate({ [channel]: enabled });
    },
    [mutation]
  );

  return {
    channels: getCachedServiceChannels(data),
    isLoading,
    isError,
    isUpdating: mutation.isPending,
    saved,
    setChannel,
  };
}
